import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Divider,
  Autocomplete,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import PurchaseRequisitionService, { PurchaseRequisition, PurchaseRequisitionItem } from '../../services/purchase-requisition.service';
import { NumberTextField } from '../../components/common';
import EmployeeService from '../../services/employee.service';
import ProductService from '../../services/product.service';
import DepartmentService from '../../services/department.service';

interface LineItem {
  tempId: string;
  id?: number;
  product: any;
  itemName: string;
  description: string;
  quantity: number;
  estimatedPrice: number;
  tax: number;
  notes: string;
}

const PurchaseRequisitionForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [employees, setEmployees] = useState<any[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  
  const [departments, setDepartments] = useState<any[]>([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null);
  
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const [lineItems, setLineItems] = useState<LineItem[]>([]);

  const [formData, setFormData] = useState<PurchaseRequisition>({
    requisitionNumber: `REQ-${Date.now()}`,
    requestedBy: 0,
    department: 0,
    requestDate: new Date().toISOString().split('T')[0],
    requiredDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'pending',
    priority: 'medium',
    notes: '',
  });

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
    fetchProducts();
    if (isEdit && id) {
      fetchRequisition(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await EmployeeService.getAll({
        'filters[status][$eq]': 'active',
        'pagination[pageSize]': 100,
      });
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await DepartmentService.getDepartments({ 'pagination[pageSize]': 100 });
      setDepartments(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchRequisition = async (reqId: number) => {
    try {
      setLoading(true);
      const response = await PurchaseRequisitionService.getOne(reqId);
      const reqData = response.data?.data;

      if (reqData) {
        const attrs = reqData.attributes;
        setFormData({
          requisitionNumber: attrs.requisitionNumber,
          requestedBy: attrs.requestedBy?.data?.id || 0,
          department: attrs.department?.data?.id || 0,
          requestDate: attrs.requestDate,
          requiredDate: attrs.requiredDate || '',
          status: attrs.status || 'pending',
          priority: attrs.priority || 'medium',
          notes: attrs.notes || '',
        });

        if (attrs.requestedBy?.data) setSelectedEmployee(attrs.requestedBy.data);
        if (attrs.department?.data) setSelectedDepartment(attrs.department.data);

        const itemsResponse = await PurchaseRequisitionService.getItems(reqId);
        const items = itemsResponse.data?.data || [];
        
        const mappedItems: LineItem[] = items.map((item: any) => ({
          tempId: `item-${item.id}`,
          id: item.id,
          product: item.attributes?.product?.data,
          itemName: item.attributes?.itemName || item.attributes?.product?.data?.attributes?.name || '',
          description: item.attributes?.description || '',
          quantity: item.attributes?.quantity || 1,
          estimatedPrice: item.attributes?.estimatedPrice || 0,
          tax: Number(item.attributes?.tax) || 5,
          notes: item.attributes?.notes || '',
        }));
        
        setLineItems(mappedItems);
      }
    } catch (error) {
      console.error('Error fetching requisition:', error);
      setError('Failed to load requisition');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addLineItem = () => {
    setLineItems([...lineItems, {
      tempId: `temp-${Date.now()}`,
      product: null,
      itemName: '',
      description: '',
      quantity: 1,
      estimatedPrice: 0,
      tax: 5,
      notes: '',
    }]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };
          if (field === 'product' && value) {
            updated.estimatedPrice = Number(value.attributes?.price) || 0;
            updated.itemName = value.attributes?.name || '';
          }
          if (field === 'quantity' || field === 'estimatedPrice' || field === 'tax') {
            updated[field] = Number(value) || 0;
          }
          return updated;
        }
        return item;
      })
    );
  };

  const validateForm = (): boolean => {
    if (!formData.requestedBy) {
      setError('Please select requested by employee');
      return false;
    }
    if (!formData.department) {
      setError('Please select a department');
      return false;
    }
    if (lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    if (lineItems.some(item => !item.product && !item.itemName)) {
      setError('All line items must have either a product selected or item name entered');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let reqId: number;

      if (isEdit && id) {
        await PurchaseRequisitionService.update(parseInt(id), formData);
        reqId = parseInt(id);
        setSuccess('Requisition updated!');
      } else {
        const response = await PurchaseRequisitionService.create(formData);
        reqId = response.data?.data?.id;
        setSuccess('Requisition created!');
      }

      if (reqId) {
        if (isEdit) {
          const oldItemsResponse = await PurchaseRequisitionService.getItems(reqId);
          const oldItems = oldItemsResponse.data?.data || [];
          await Promise.all(oldItems.map((item: any) => PurchaseRequisitionService.deleteItem(item.id)));
        }

        await Promise.all(
          lineItems.map((item) =>
            PurchaseRequisitionService.createItem({
              requisition: reqId,
              product: item.product?.id,
              itemName: item.itemName || item.product?.attributes?.name,
              description: item.description,
              quantity: item.quantity,
              estimatedPrice: item.estimatedPrice,
              tax: item.tax ?? 5,
              notes: item.notes,
            })
          )
        );
      }

      setTimeout(() => navigate('/purchase-requisitions'), 1500);
    } catch (error: any) {
      console.error('Error saving requisition:', error);
      setError(error.response?.data?.error?.message || 'Failed to save requisition');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totalEstimated = lineItems.reduce(
    (sum, item) => sum + (item.quantity * item.estimatedPrice) * (1 + (item.tax || 0) / 100),
    0
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Purchase Requisition' : 'New Purchase Requisition'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Requisition Details</Typography>
            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Requisition Number"
                  name="requisitionNumber"
                  value={formData.requisitionNumber}
                  onChange={handleChange}
                  disabled={isEdit}
                />
                <TextField
                  select
                  fullWidth
                  label="Priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </TextField>
              </Stack>

              <Autocomplete
                value={selectedEmployee}
                onChange={(e, v) => {
                  setSelectedEmployee(v);
                  setFormData({ ...formData, requestedBy: v?.id || 0 });
                }}
                options={employees}
                loading={employeesLoading}
                getOptionLabel={(o) => `${o.attributes?.firstName} ${o.attributes?.lastName}`}
                renderInput={(params) => <TextField {...params} label="Requested By *" required />}
              />

              <Autocomplete
                value={selectedDepartment}
                onChange={(e, v) => {
                  setSelectedDepartment(v);
                  setFormData({ ...formData, department: v?.id || 0 });
                }}
                options={departments}
                loading={departmentsLoading}
                getOptionLabel={(o) => o.attributes?.name || ''}
                renderInput={(params) => <TextField {...params} label="Department *" required />}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Request Date"
                  name="requestDate"
                  type="date"
                  value={formData.requestDate}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="Required Date"
                  name="requiredDate"
                  type="date"
                  value={formData.requiredDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Items</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addLineItem} size="small">
                Add Item
              </Button>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="20%">Product (Optional)</TableCell>
                    <TableCell width="20%">Item Name *</TableCell>
                    <TableCell width="10%">Qty</TableCell>
                    <TableCell width="12%">Est. Price</TableCell>
                    <TableCell width="10%">Tax %</TableCell>
                    <TableCell width="13%">Total</TableCell>
                    <TableCell width="10%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No items added. Click "Add Item" to start.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    lineItems.map((item) => (
                      <TableRow key={item.tempId}>
                        <TableCell>
                          <Autocomplete
                            value={item.product}
                            onChange={(e, v) => updateLineItem(item.tempId, 'product', v)}
                            options={products}
                            loading={productsLoading}
                            getOptionLabel={(o) => `${o.attributes?.name} (${o.attributes?.sku})`}
                            renderInput={(params) => <TextField {...params} size="small" placeholder="Optional" />}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.itemName}
                            onChange={(e) => updateLineItem(item.tempId, 'itemName', e.target.value)}
                            placeholder="Item name *"
                            required={!item.product}
                            fullWidth
                            helperText={!item.product && !item.itemName ? 'Required if no product' : ''}
                            error={!item.product && !item.itemName}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(item.tempId, 'quantity', e.target.value)}
                            inputProps={{ min: 1 }}
                            fullWidth
                            allowDecimal={false}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.estimatedPrice}
                            onChange={(e) => updateLineItem(item.tempId, 'estimatedPrice', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <NumberTextField
                            size="small"
                            value={item.tax}
                            onChange={(e) => updateLineItem(item.tempId, 'tax', e.target.value)}
                            inputProps={{ min: 0, max: 100, step: 0.01 }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            OMR {((item.quantity * item.estimatedPrice) * (1 + (item.tax || 0) / 100)).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removeLineItem(item.tempId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h5">Total Estimated:</Typography>
                <Typography variant="h4" color="primary" fontWeight="bold">
                  OMR {totalEstimated.toFixed(2)}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/purchase-requisitions')} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Requisition' : 'Create Requisition'}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Box>
  );
};

export default PurchaseRequisitionForm;

